import { useMemo } from 'react'
import { NavigateFunction } from 'react-router-dom'
import moment, { Moment } from 'moment-timezone'
import { getUrl } from '@Builder/QuoteDetailPage/FilesTab/ConfigurationModal/logic'
import { THREADS_KEY } from '@Builder/QuoteDetailPage/FilesTab/ConfigurationModal/constants'
import {
    shipmentLines_shipment_shipmentLines_items_inspectionDocuments as InspectionDocumentsType,
    shipmentLines_shipment_shipmentLines_items as ItemsType,
    shipmentLines_shipment_shipmentLines_items_boundingBox as BoundingBoxType,
} from '@Orders/OrderDetailsPage/Queries/__generated__/shipmentLines'

import getColor from '~/lib/getColor'
import {
    TAPS_INSPECTION_MODE,
    SELECT_MODE,
    DFM_INSPECTION_MODE,
} from '~/redux-modules/qmr-viewer/constants'
import {
    ProcessType,
    MaterialType,
} from '~/apps/consumer/components/PartViewerModal/types'
export const MM_TO_IN = 25.4
export const DATE_FORMAT = 'ddd, MMM DD [at] h:mm A'

export const PART_DETAILS_PAGES = {
    PART_DETAILS: 'Part Details',
    INSPECTION_REPORT: 'Inspection report',
    THREADS: 'Threads',
    MANUFACTURING_FEEDBACK: 'Manufacturability feedback',
    CONFIGURATION: 'configuration',
    SUMMARY: 'Summary',
    TRACK_SHIPMENT: 'Track shipment',
} as const

export const INSPECTION_TYPES = {
    DIMENSIONAL_INSPECTION: 'dimensional',
    VISUAL_INSPECTION: 'visual',
} as const

export const ATTACHMENT_FILE_TYPE = {
    IMAGE: 'image',
    FILE: 'application',
} as const

export const DFM_FEEDBACK_KEY = 'feedback'
export const INSPECTION_KEY = 'inspection'
export const SUMMARY_KEY = 'summary'

export const TRACK_SHIPMENT_KEY = 'track-shipment'
export const BLACK_ALPHA_BOTTOM = `1px solid ${getColor('blackAlpha15')}`

export const NO_INSPECTION_DOCUMENTS = 'No inspection documents available'

export const MANUFACTURING_DRAWING_CONTENT = `The manufacturing drawing is sent to our manufacturing partners
                and includes inspection bubbles, notes, and private information
                is redacted where applicable.`

export const PURCHASE_DRAWING_CONTENT = `The purchased drawing represents the state of your drawing when
                you placed the order.`

export const PURCHASE_DRAWING_CONTENT_TITLE = 'What is a purchased drawing?'

export const MANUFACTURING_DRAWING_CONTENT_TITLE =
    'What is a manufacturing drawing?'

export const PURCHASE_DRAWING = 'Purchase drawing'
export const MANUFACTURING_DRAWING = 'Manufacturing drawing'

export const NO_INSPECTON_DRAWNG =
    'Manufacturing drawing is not ready, check back soon.'
export const MAX_LENGTH_FILENAME = 35

export const COLOR_TEXT = getColor('blackAlpha45')
export const HEADER_STYLES = {
    color: getColor('blackAlpha85'),
    fontSize: '14px',
    fontWeight: '500',
    lineHeight: '22px',
    marginBottom: '8px',
}
export const INSPECTION_CRITERIA_LINK =
    'https://docsend.com/view/kbgvan9jxjmtnmgj'

export const INSPECTION_DOCUMENT_TYPE = {
    FICTIV5: 'fictiv5',
    PART_PHOTO: 'partPhoto',
    ADDITIONAL_DOC: 'additionalDoc',
    MANUFACTURING_DRAWING: 'manufacturingDrawing',
    ADVANCED_INSPECTION: 'advancedInspection',
    MATERIAL_CERTIFICATION: 'materialCertification',
    CERTIFICATE_OF_CONFORMANCE: 'certificateOfConformance',
    UNKNOWN: 'unknown',
} as const

const isInspectionDocument = (type: string) => {
    const TYPES: string[] = [
        INSPECTION_DOCUMENT_TYPE.FICTIV5,
        INSPECTION_DOCUMENT_TYPE.ADVANCED_INSPECTION,
    ]

    return TYPES.includes(type)
}

const isCertificate = (type: string) => {
    const TYPES: string[] = [
        INSPECTION_DOCUMENT_TYPE.MATERIAL_CERTIFICATION,
        INSPECTION_DOCUMENT_TYPE.CERTIFICATE_OF_CONFORMANCE,
    ]

    return TYPES.includes(type)
}

export function setBoundingBox(boundingBox: BoundingBoxType): string {
    const { width, length, thickness } = boundingBox

    return `${width.toFixed(3)} X ${length.toFixed(3)} X ${thickness.toFixed(
        3,
    )}`
}

export function handleUrl(
    key: string,
    activePage: string,
    navigate: NavigateFunction,
) {
    if (location.pathname.toString().indexOf('part') != -1) {
        const url = getUrl(location.pathname, activePage)

        navigate(`${url}/${key}`)
    }
}

export function docPhotosOnly(doc: InspectionDocumentsType): boolean {
    return !!doc?.mimeType?.includes(ATTACHMENT_FILE_TYPE.IMAGE)
}

export const getInputStyle = (isDefault: boolean) =>
    isDefault
        ? {
              CURSOR: 'default',
              COLOR: getColor('grayMedium'),
          }
        : {
              CURSOR: 'pointer',
              COLOR: getColor('black'),
          }

export const formatDateForInspectionDocument = (date: Date | Moment) =>
    moment(date).format(DATE_FORMAT)

export const useInspectionType = (
    inspectionTypes?: ItemsType['inspectionTypes'],
) =>
    useMemo(
        () =>
            inspectionTypes &&
            inspectionTypes.length >= 1 &&
            inspectionTypes.every(
                type => type === INSPECTION_TYPES.VISUAL_INSPECTION,
            )
                ? INSPECTION_TYPES.VISUAL_INSPECTION
                : INSPECTION_TYPES.DIMENSIONAL_INSPECTION,
        [inspectionTypes],
    )

const getImages = (inspectionDocuments: InspectionDocumentsType[]) =>
    inspectionDocuments.filter(
        inspectionDocument =>
            inspectionDocument.downloadUrl &&
            !isInspectionDocument(inspectionDocument.type) &&
            !isCertificate(inspectionDocument.type) &&
            inspectionDocument.mimeType &&
            inspectionDocument.mimeType.startsWith(ATTACHMENT_FILE_TYPE.IMAGE),
    )

const getFiles = (inspectionDocuments: InspectionDocumentsType[]) =>
    inspectionDocuments.filter(
        inspectionDocument =>
            inspectionDocument.downloadUrl &&
            !isInspectionDocument(inspectionDocument.type) &&
            !isCertificate(inspectionDocument.type) &&
            inspectionDocument.type !==
                INSPECTION_DOCUMENT_TYPE.MANUFACTURING_DRAWING &&
            inspectionDocument.mimeType &&
            inspectionDocument.mimeType.startsWith(ATTACHMENT_FILE_TYPE.FILE),
    )

const getFictiv5s = (inspectionDocuments: InspectionDocumentsType[]) =>
    inspectionDocuments.filter(
        inspectionDocument =>
            inspectionDocument.downloadUrl &&
            isInspectionDocument(inspectionDocument.type),
    )

const getCertificates = (inspectionDocuments: InspectionDocumentsType[]) =>
    inspectionDocuments.filter(
        inspectionDocument =>
            inspectionDocument.downloadUrl &&
            isCertificate(inspectionDocument.type),
    )

const getManufacturingDrawing = (
    inspectionDocuments: InspectionDocumentsType[],
) =>
    inspectionDocuments.filter(
        inspectionDocument =>
            inspectionDocument.downloadUrl &&
            inspectionDocument.type ===
                INSPECTION_DOCUMENT_TYPE.MANUFACTURING_DRAWING,
    )

export const usePhotosAndAdditionalDocs = (
    inspectionDocuments?: InspectionDocumentsType[],
) =>
    useMemo(
        () => ({
            images: inspectionDocuments ? getImages(inspectionDocuments) : [],
            files: inspectionDocuments ? getFiles(inspectionDocuments) : [],
            fictiv5s: inspectionDocuments
                ? getFictiv5s(inspectionDocuments)
                : [],
            manufacturingDrawings: inspectionDocuments
                ? getManufacturingDrawing(inspectionDocuments)
                : [],
            certificates: inspectionDocuments
                ? getCertificates(inspectionDocuments)
                : [],
        }),
        [inspectionDocuments],
    )

export const useSelectedDoc = (
    inspectionDocumentIdx: number,
    fictiv5sAndPhotos: InspectionDocumentsType[],
    fictiv5s: InspectionDocumentsType[],
    images: InspectionDocumentsType[],
    files: InspectionDocumentsType[],
) =>
    useMemo(() => {
        let selectedDoc

        if (fictiv5sAndPhotos.length > 0) {
            selectedDoc = fictiv5sAndPhotos[inspectionDocumentIdx]
        } else if (fictiv5s.length + images.length > 0) {
            selectedDoc = fictiv5s.concat(images)[inspectionDocumentIdx]
        } else {
            selectedDoc = files[inspectionDocumentIdx]
        }
        return selectedDoc || ({} as InspectionDocumentsType)
    }, [inspectionDocumentIdx, fictiv5sAndPhotos, fictiv5s, images, files])

export const usePostProcessesInfo = (
    primaryProcess: string | null,
    postProcesses: Partial<ProcessType | null>[] | null,
) =>
    useMemo(() => {
        const filteredPostProcesses =
            postProcesses && postProcesses.length > 1
                ? postProcesses.filter(
                      process =>
                          process &&
                          primaryProcess &&
                          !process.processName?.includes(primaryProcess),
                  )
                : null

        const postProcessesToDisplay = filteredPostProcesses || []

        return postProcessesToDisplay
    }, [primaryProcess, postProcesses])

export const useThreeDviewerMode = (activePage?: string | null) =>
    useMemo(() => {
        if (
            activePage === PART_DETAILS_PAGES.THREADS ||
            activePage === THREADS_KEY
        ) {
            return TAPS_INSPECTION_MODE
        } else if (activePage === PART_DETAILS_PAGES.MANUFACTURING_FEEDBACK) {
            return DFM_INSPECTION_MODE
        } else if (
            activePage === PART_DETAILS_PAGES.PART_DETAILS ||
            activePage === PART_DETAILS_PAGES.CONFIGURATION
        ) {
            return SELECT_MODE
        }
        return SELECT_MODE
    }, [activePage])

export const transformAttributeDetails = ({
    color,
    thickness,
    texture,
    shore,
}: {
    color?: string | null
    thickness?: string | null
    texture?: string | null
    shore?: string | null
}): string => {
    const attributes = []
    let attributesDetails = ''

    if (color) {
        attributes.push(color)
    }
    if (thickness) {
        attributes.push(`${thickness}"`)
    }
    if (texture) {
        attributes.push(texture)
    }
    if (shore) {
        attributes.push(shore)
    }
    if (attributes.length > 0) {
        attributesDetails = `(${attributes.join(', ')})`
    }
    return attributesDetails
}

export const getPrimaryProcessMaterialDetails = (
    processes: Partial<ProcessType | null>[] | null,
    material?: MaterialType | null,
    materialName?: string,
) => {
    const primaryProcess = processes && processes[0]
    let details = ''

    if (primaryProcess && (material || materialName)) {
        const materialDisplayName = material
            ? material.publicName || material.name
            : materialName

        details += `${primaryProcess.processName} - ${materialDisplayName} `
        details += transformAttributeDetails({
            color: material?.color,
            shore: material?.shore,
            texture: primaryProcess?.texture,
            thickness: primaryProcess?.thickness,
        })
    }

    return details
}
